<script setup lang="ts">
import { useNavigation } from '@base/composables/navigation'
import { ut_getText, ut_isPrint, ut_isScreenSize, ut_navigateNewRoute } from '@base/utils/utils'
import { useAppIndexStore } from '@base/stores/indexStore'
import type { TabsItem } from '#ui/types'
import type { AccordionItem } from '@nuxt/ui'
import { EScreenSize } from '@base/utils/constants'

defineProps<{
  isInternal: boolean
  realCountry: string
  countryLanguage: string
  showModalPdf: boolean
}>()

const emit = defineEmits<{ 'update:modal-close': [boolean] }>()

const cp = useNavigation()
const indexStore = useAppIndexStore()
const selectedTab: Ref<string> = ref(getActiveTab(true))
const selectedAccordion: Ref<string> = ref(getActiveTab(false))

// Video URL depending on language
function getVideo(): string {
  let res: string
  switch (indexStore.state.paramData.lcda) {
    case 'de':
      res = 'https://cms.media.wilo.com//resources/video7/video_embed.html?url=/cdnvidhls/wilo629293/8069842/video_config.json&autostart=false&defaultquality=0&mute=false'
      break
    case 'nl':
      res = 'https://cms.media.wilo.com/resources/video7/video_embed.html?url=/cdnvidhls/wilo654879/8284091/video_config.json&autostart=false&defaultquality=0&mute=false&st=nl'
      break
    case 'fr':
      res = 'https://cms.media.wilo.com/resources/video7/video_embed.html?url=/cdnvidhls/wilo655053/8350582/video_config.json&autostart=false&defaultquality=0&mute=false&st=fr'
      break
    default:
      res = 'https://cms.media.wilo.com/resources/video7/video_embed.html?url=/cdnvidhls/wilo634477/8289253/video_config.json&autostart=false&defaultquality=0&mute=false&st=auto'
  }
  return res
}

// QS has 2 tabs use series configs
function getQSTabItems(): TabsItem[] | AccordionItem[] {
  const res: TabsItem[] = []
  if (cp.navigationData.value) {
    for (const node of cp.navigationData.value || []) {
      if (!node.desc.includes('(')) { // should be done by baseobj
        const tab: TabsItem = {
          label: node.desc,
          value: node.nodeName,
        }
        res.push(tab)
      }
    }
  }
  return res
}

// Active tab default or in paramData
function getActiveTab(useDefault: boolean): string {
  let res: string = ''
  if (indexStore.state.paramData.tab) {
    res = indexStore.state.paramData.tab
  }
  else if (useDefault) {
    const tabs: TabsItem[] = getQSTabItems()
    if (tabs[0] && tabs[0].value) {
      res = tabs[0].value.toString()
    }
  }
  return res
}

// Add tab param to URL Tabs
function changeQSTab() {
  selectedAccordion.value = selectedTab.value
  ut_navigateNewRoute('', { tab: selectedTab.value }, '', false)
}

// Add tab param to URL Accordion
function changeQSAccordion() {
  if (selectedAccordion.value) {
    selectedTab.value = selectedAccordion.value
  }
  ut_navigateNewRoute('', { tab: selectedAccordion.value || '' }, '', false)
}
</script>

<template>
  <template v-if="cp.navigationData.value">
    <template v-if="!ut_isPrint()">
      <UContainer>
        <div class="flex flex-wrap justify-between items-center mb-4">
          <h1 class="wrap-break-word" v-html="indexStore.state.currentNode.desc" />
          <div class="-mx-2">
            <LazySettingsSmall :is-historic="false" :real-country="realCountry" :is-internal="isInternal" :show-frequency="false" />
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div v-html="ut_getText('txt_qs-intro')" />
          <iframe :src="getVideo()" class="w-full aspect-video my-auto" loading="lazy" allowfullscreen />
        </div>
      </UContainer>

      <!-- Desktop view -->
      <div v-if="!ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm])" class="hidden md:block relative">
        <div class="absolute top-[50px] left-0 w-full h-[6px] bg-gunmetal-4" />
        <UContainer>
          <UTabs
            v-model="selectedTab"
            :items="getQSTabItems()"
            :unmount-on-hide="false"
            @update:model-value="changeQSTab()"
          >
            <template #content="{ item }">
              <QuickSelectInput
                :app-type="item.value?.toString() || ''"
                :real-country="realCountry"
                :country-language="countryLanguage"
                :is-internal="isInternal"
              />
            </template>
          </UTabs>
        </UContainer>
      </div>
      <!-- Mobile view -->
      <div v-else class="block md:hidden">
        <UContainer>
          <UAccordion
            v-model="selectedAccordion"
            trailing-icon="i-wilo-xmark-solid-full"
            :items="getQSTabItems() as AccordionItem[]"
            :unmount-on-hide="false"
            @update:model-value="changeQSAccordion()"
          >
            <template #content="{ item }">
              <QuickSelectInput
                :app-type="item.value?.toString() || ''"
                :real-country="realCountry"
                :country-language="countryLanguage"
                :is-internal="isInternal"
              />
            </template>
          </UAccordion>
        </UContainer>
      </div>
    </template>

    <!-- Print -->
    <template v-if="ut_isPrint()">
      <!-- ToDo not yet done -->
      <!--      <template v-for="(partTable, index) in ut_wilo_compareTablePrintGroups(compareTable[selectedTab], 3)" :key="index">
      </template> -->
    </template>
  </template>

  <LazyModalPDF
    v-if="showModalPdf"
    :modal-open="showModalPdf"
    :real-country="realCountry"
    :special-title="ut_getText('txt_qs-title')"
    :is-quick-select="true"
    @update:close="emit('update:modal-close', true)"
  />
</template>
